import { type TextureSource } from '../rendering/Texture';

/**
 * Fetches an image and resolves once it is decoded and ready to upload.
 *
 * A function rather than an interface because there is exactly one thing to do; injecting it is what
 * lets the asset manager be driven by a stub with no network and no DOM.
 */
export type ImageLoad = (url: string) => Promise<TextureSource>;

/** Fetches a URL as raw bytes, for assets the engine decodes itself. */
export type BytesLoad = (url: string) => Promise<ArrayBuffer>;

/**
 * Loads an image through an `HTMLImageElement`.
 *
 * `decode()` rather than the `load` event, so the promise resolves when the pixels are actually ready
 * and the first frame that draws the sprite cannot stall on decoding.
 */
export async function loadImageElement(url: string): Promise<TextureSource> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = url;

  try {
    await image.decode();
  } catch (cause) {
    throw new Error(`Could not load the image asset at "${url}".`, { cause });
  }
  return image;
}

/** Fetches raw bytes, failing loudly on an error status rather than decoding an HTML error page. */
export async function fetchBytes(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Asset request for "${url}" failed with status ${String(response.status)}.`);
  }
  return response.arrayBuffer();
}
